import { User } from 'oidc-client';
import { OidcService } from '../../types/oidc-service';
import Claims from '../../types/claims';
import { BaseOidcService } from '../base-oidc/base-oidc-service';

// Implements Hybrid OIDC flow
export class HybridOidcService extends BaseOidcService implements OidcService {
    
    public init = async (): Promise<void> => {
        return Promise.resolve();
    }

    public isAuthenticated = (): boolean => {
        const authKey = `oidc.user:${this.oidcConfig?.authority}:${this.oidcConfig?.client_id}`;
        const authTokenJson = localStorage.getItem(authKey);

        if (authTokenJson) {
            const authToken = JSON.parse(authTokenJson);
            return !!authToken && !!authToken.access_token;
        }

        return false;
    };

    public checkSession = async (): Promise<boolean> => {
        const url = this.oidcMetadataConfig?.userinfo_endpoint;
        const accessToken = await this.getAccessToken();

        if(!accessToken || !url) return false;

        return this.fetchUserInfo(url, accessToken)
            .then(() => { return true })
            .catch(error => {
                switch(error.status) {
                    case 401: 
                        return false;
                    default: 
                        throw new Error("Unable to check auth session.");
                }
            });
    };

    public getAccessToken = async (): Promise<string | null> => {
        const user = await this.getUser();
        return user ? user.access_token : null;
    };

    public getClaims = async (): Promise<Claims> => {
        const user = await this.getUser();
        return this.mapClaims(user?.profile);
    };

    public signinRedirect = async (): Promise<void> => {
        await this.userManager?.clearStaleState();
        await this.userManager?.signinRedirect(
            { state: window.location.pathname + window.location.search }
        );
    };

    public signinRedirectCallback = async (): Promise<void> => {
        const user = await this.userManager?.signinRedirectCallback();

        if(!user) return;

        if(typeof user.state === 'string') {
            const url = new URL('http://domain.com' + user.state);
            const claims = this.mapClaims(user?.profile);
            const locationId = url.searchParams.get('locationId');
            const customerId = url.searchParams.get('customerId');

            if(claims.userClass !== 'GlobalUser' && claims.userClass !== 'PattersonUser' &&
                ((locationId && !claims.availableLocations?.includes(locationId))
                    || (customerId && claims.masterAccountId?.toString() !== customerId))) 
            {
                this.logout()
            }
        }

        window.location.replace(user.state);
    };

    public logout = async (): Promise<void> => {
        await this.userManager?.clearStaleState();
        await this.userManager?.signoutRedirect();
        if(this.logoutHandler) {
            this.logoutHandler();
        }
    };

    public onLogout(handler: () => void) {
        this.logoutHandler = handler;
    }

    private getUser = async (): Promise<User | null> => {
        return await this.userManager?.getUser() ?? null;
    };
}
